import type { IconType } from '@kognitos/lattice';
import i18n from '@/shared/i18n';

export const UserIntent = {
  AUTOMATING_WORKFLOWS: i18n.t(
    'onboarding.options.userIntent.automatingWorkflows',
    { ns: 'onboarding' },
  ),
  ANALYTICS_REPORTING: i18n.t(
    'onboarding.options.userIntent.analyticsReporting',
    { ns: 'onboarding' },
  ),
  SCALING_PROCESSES: i18n.t('onboarding.options.userIntent.scalingProcesses', {
    ns: 'onboarding',
  }),
  OTHER: i18n.t('onboarding.options.userIntent.other', { ns: 'onboarding' }),
} as const;

export const WorkspaceDomain = {
  FINANCE: i18n.t('onboarding.options.workspaceDomain.finance', {
    ns: 'onboarding',
  }),
  MARKETING: i18n.t('onboarding.options.workspaceDomain.marketing', {
    ns: 'onboarding',
  }),
  ACCOUNTING: i18n.t('onboarding.options.workspaceDomain.accounting', {
    ns: 'onboarding',
  }),
  HUMAN_RESOURCES: i18n.t('onboarding.options.workspaceDomain.humanResources', {
    ns: 'onboarding',
  }),
  IT_ENGINEERING: i18n.t('onboarding.options.workspaceDomain.itEngineering', {
    ns: 'onboarding',
  }),
  OPERATIONS: i18n.t('onboarding.options.workspaceDomain.operations', {
    ns: 'onboarding',
  }),
  SALES: i18n.t('onboarding.options.workspaceDomain.sales', {
    ns: 'onboarding',
  }),
  CUSTOMER_SUPPORT: i18n.t(
    'onboarding.options.workspaceDomain.customerSupport',
    { ns: 'onboarding' },
  ),
  LEGAL_COMPLIANCE: i18n.t(
    'onboarding.options.workspaceDomain.legalCompliance',
    { ns: 'onboarding' },
  ),
  OTHER: i18n.t('onboarding.options.workspaceDomain.other', {
    ns: 'onboarding',
  }),
} as const;

export const WorkspaceColor = {
  GRAY: '#6B7280',
  RED: '#EF4444',
  ORANGE: '#F97316',
  YELLOW: '#EAB308',
  GREEN: '#22C55E',
  CYAN: '#06B6D4',
  BLUE: '#3B82F6',
  PURPLE: '#8B5CF6',
  PINK: '#EC4899',
} as const;

export const WorkspaceIcon = {
  GRIP: 'Grip',
  BUILDING: 'Building',
  BRIEFCASE_BUSINESS: 'BriefcaseBusiness',
  LAYERS3: 'Layers3',
  NETWORK: 'Network',
  FOLDER_OPEN: 'FolderOpen',
  BAR_CHART_BIG: 'BarChartBig',
  PIE_CHART: 'PieChart',
  DOLLAR_SIGN: 'DollarSign',
  USERS: 'Users',
  HANDSHAKE: 'Handshake',
  LAPTOP_MINIMAL: 'LaptopMinimal',
  DATABASE: 'Database',
  SERVER: 'Server',
  SHIELD_CHECK: 'ShieldCheck',
  FILE_COG: 'FileCog',
  ROTATE_CW_SQUARE: 'RotateCwSquare',
  ZAP: 'Zap',
  ROCKET: 'Rocket',
  CLOCK4: 'Clock4',
  LOCATE_FIXED: 'LocateFixed',
  PACKAGE: 'Package',
  BOOK_OPEN: 'BookOpen',
} as const;

export const JobRole = {
  AUTOMATION_RPA_LEAD: i18n.t('onboarding.options.jobRole.automationRpaLead', {
    ns: 'onboarding',
  }),
  EXECUTIVE_LEADERSHIP: i18n.t(
    'onboarding.options.jobRole.executiveLeadership',
    { ns: 'onboarding' },
  ),
  IT_SYSTEMS_ADMIN: i18n.t('onboarding.options.jobRole.itSystemsAdmin', {
    ns: 'onboarding',
  }),
  ENGINEERING_DEVELOPER: i18n.t(
    'onboarding.options.jobRole.engineeringDeveloper',
    { ns: 'onboarding' },
  ),
  DATA_ANALYTICS: i18n.t('onboarding.options.jobRole.dataAnalytics', {
    ns: 'onboarding',
  }),
  OPERATIONS: i18n.t('onboarding.options.jobRole.operations', {
    ns: 'onboarding',
  }),
  FINANCE: i18n.t('onboarding.options.jobRole.finance', {
    ns: 'onboarding',
  }),
  ACCOUNTING: i18n.t('onboarding.options.jobRole.accounting', {
    ns: 'onboarding',
  }),
  COMPLIANCE_AUDIT: i18n.t('onboarding.options.jobRole.complianceAudit', {
    ns: 'onboarding',
  }),
  PRODUCT_MANAGEMENT: i18n.t('onboarding.options.jobRole.productManagement', {
    ns: 'onboarding',
  }),
  OTHER: i18n.t('onboarding.options.jobRole.other', { ns: 'onboarding' }),
} as const;

// Simple arrays for components (as requested by user)
export const USER_INTENTS: string[] = Object.values(UserIntent);
export const WORKSPACE_DOMAINS: string[] = Object.values(WorkspaceDomain);
export const WORKSPACE_COLORS: string[] = Object.values(WorkspaceColor);
export const WORKSPACE_ICONS: IconType[] = Object.values(
  WorkspaceIcon,
) as IconType[];
export const JOB_ROLES: string[] = Object.values(JobRole);

// Type definitions for better type safety
export type UserIntentType = (typeof UserIntent)[keyof typeof UserIntent];
export type WorkspaceDomainType =
  (typeof WorkspaceDomain)[keyof typeof WorkspaceDomain];
export type WorkspaceColorType =
  (typeof WorkspaceColor)[keyof typeof WorkspaceColor];
export type WorkspaceIconType =
  (typeof WorkspaceIcon)[keyof typeof WorkspaceIcon];
export type JobRoleType = (typeof JobRole)[keyof typeof JobRole];
